import { FormControl } from '@chakra-ui/react';
import { useClassFiltersStore } from '@stores/classes';
import { useListLocations } from 'hooks/useApiQueries';
import { useCoachStudioParams } from 'hooks/useCoachStudioParams';
import { useLayoutEffect, useRef } from 'react';
import { useTranslation } from 'react-i18next';
import { Select } from 'chakra-react-select';
import { ChakraStylesConfig } from 'chakra-react-select';
import { useNavigate } from 'react-router-dom';

interface StudioOption {
  label: string;
  value: number;
}

export function ClassesFiltersStudios() {
  const { t } = useTranslation();
  const { data: studios } = useListLocations();
  const studioIds = useClassFiltersStore((state) => state.studioIds);
  const setStudioIds = useClassFiltersStore((state) => state.setStudioIds);
  const { isStudioUrl, id } = useCoachStudioParams();
  const navigate = useNavigate();

  // Flag pour empêcher le useLayoutEffect de récraser la sélection manuelle
  const isManualChange = useRef(false);

  // Mettre à jour le studioId lorsque l'URL contient un studio
  useLayoutEffect(() => {
    // Ne pas forcer si l'utilisateur vient de faire un changement manuel
    if (isStudioUrl && !isManualChange.current) {
      setStudioIds([id]);
    }
    // Reset le flag après chaque render
    isManualChange.current = false;
  }, [isStudioUrl, id, setStudioIds]);

  const options: StudioOption[] = Array.isArray(studios)
    ? [
        ...studios
          .filter((studio) => {
            if (!mondayMindbody?.siteId) return true;
            if (mondayMindbody.siteId === '5736526' && studio.Id === 9) return false;
            return mondayMindbody.siteId === String(studio.SiteID);
          })
          .map((studio) => ({
            label: studio.Name.replace(/^(Dynamo|Risse|Punch)\s*/i, 'Studio '),
            value: studio.Id,
          })),
      ]
    : [];

  const values = options.filter((option) => studioIds.includes(option.value));

  const chakraStyles: ChakraStylesConfig<StudioOption, true> = {
    container: (provided) => ({
      ...provided,
      minWidth: '200px',
    }),
    control: (provided) => ({
      ...provided,
      backgroundColor: 'white',
      borderColor: 'gray.200',
      _hover: {
        borderColor: 'primary.500',
      },
    }),
    option: (provided, state) => ({
      ...provided,
      backgroundColor: state.isSelected ? 'primary.500' : 'white',
      color: state.isSelected ? 'white' : 'gray.700',
      _hover: {
        backgroundColor: state.isSelected ? 'primary.600' : 'gray.100',
      },
    }),
    multiValue: (provided) => ({
      ...provided,
      backgroundColor: 'primary.100',
      fontSize: '16px',
      padding: '5px',
    }),
    multiValueLabel: (provided) => ({
      ...provided,
      color: 'primary.700',
    }),
  };

  return (
    <FormControl>
      <Select<StudioOption, true>
        size="sm"
        className="studioSelect"
        isMulti
        name="studios"
        options={options}
        placeholder={t('Sélectionner un ou plusieurs studios')}
        closeMenuOnSelect={false}
        value={values}
        onChange={(options) => {
          const selectedIds = options ? options.map((option) => option.value) : [];

          // Marquer ce changement comme manuel
          isManualChange.current = true;

          // Mettre à jour le store AVANT de naviguer
          setStudioIds(selectedIds);

          // Si on est sur une URL /studio/:id et que l'utilisateur modifie la sélection
          // on nettoie l'URL pour repasser en mode "normal"
          if (isStudioUrl) {
            navigate('/', { replace: true });
          }
        }}
        chakraStyles={chakraStyles}
      />
    </FormControl>
  );
}
